import { useCallback, useEffect, useRef, useState } from "react";
import { useDragDropManager } from "react-dnd";
import { useSelector } from "react-redux";
import { EMPTY, interval, map, takeWhile } from "rxjs";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { selectRefreshToken } from "../../../../store/selectors/currentReportSelectors.ts";

interface Props<T extends ApiResponse<R>, R> {
  paused?: boolean;
  request: (onBuildDone: (data: T) => void, onBuildError: (reason: string | undefined) => void) => void;
  cancelRequest: () => void;
  handleBuildDone: (data: T) => void;
  handleBuildError: (reason: string | undefined) => void;
  onProgressChange: (progress: number | undefined) => void;
}

interface Trigger {
  prevRefreshToken: string | undefined;
  currentRefreshToken: string;
}

const isManuallyTriggered = (trigger: Trigger) => (trigger.prevRefreshToken ?? "") !== trigger.currentRefreshToken;

const DELAY_MS = 2500;
const MAX_PROGRESS_VALUE = 100;
const PROGRESS_STEP = 5;
const intervalMs = DELAY_MS / (MAX_PROGRESS_VALUE / PROGRESS_STEP);

const useDeferredBuild = <T extends ApiResponse<R>, R>({
  paused = false,
  request,
  cancelRequest,
  handleBuildDone,
  onProgressChange,
  handleBuildError,
}: Props<T, R>) => {
  const dragDropManager = useDragDropManager();
  const refreshToken = useSelector(selectRefreshToken);

  const [buildResult, setBuildResult] = useState<T>();
  const [isDragging, setIsDragging] = useState(false);
  const [trigger, setTrigger] = useState<Trigger>();

  const isInitialRequestRef = useRef(true);
  const requestStatusRef = useRef<"pending" | "paused" | undefined>(undefined);
  const memoizedActionsRef = useRef({
    request,
    cancelRequest,
    handleBuildDone,
    onProgressChange,
    handleBuildError,
  });
  useEffect(() => {
    memoizedActionsRef.current = { request, cancelRequest, handleBuildDone, onProgressChange, handleBuildError };
  }, [cancelRequest, handleBuildDone, handleBuildError, onProgressChange, request]);

  const resetInitialRequestFlag = useCallback(() => (isInitialRequestRef.current = true), []);

  const doRequest = useCallback((refreshToken: string) => {
    requestStatusRef.current = "pending";
    setTrigger((prev) => ({
      prevRefreshToken: prev?.currentRefreshToken,
      currentRefreshToken: refreshToken,
    }));
  }, []);

  const onBuildDone = useCallback((data: T) => {
    setBuildResult(data);
  }, []);

  const onBuildError = useCallback((reason: string | undefined) => {
    memoizedActionsRef.current.handleBuildError(reason);
    memoizedActionsRef.current.onProgressChange(undefined);
  }, []);

  const cancelCurrentAwaiter = useCallback(() => {
    requestStatusRef.current = undefined;
    setTrigger(undefined);
    memoizedActionsRef.current.onProgressChange(undefined);
  }, []);

  useEffect(() => {
    if (buildResult === undefined) {
      return;
    }
    if (!isDragging) {
      memoizedActionsRef.current.handleBuildDone(buildResult);
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setBuildResult(undefined);
    }
  }, [buildResult, isDragging]);

  useEffect(() => {
    // Build can also end adding mode; wait for its new trigger before resuming a paused request.
    if (!trigger || !requestStatusRef.current || trigger.currentRefreshToken !== refreshToken) {
      memoizedActionsRef.current.onProgressChange(undefined);
      return;
    }

    if (paused && (trigger.prevRefreshToken === undefined || !isManuallyTriggered(trigger))) {
      requestStatusRef.current = "paused";
      memoizedActionsRef.current.onProgressChange(undefined);
      return;
    }

    const shouldDelayRequest =
      requestStatusRef.current !== "paused" && isInitialRequestRef.current === false && !isManuallyTriggered(trigger);

    if (shouldDelayRequest) {
      memoizedActionsRef.current.onProgressChange(undefined);
    }

    let progress = 0;
    let progressResetTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const subscription = (
      shouldDelayRequest
        ? interval(intervalMs).pipe(
            map(() => (progress += PROGRESS_STEP)),
            takeWhile((value) => value <= MAX_PROGRESS_VALUE)
          )
        : EMPTY
    ).subscribe({
      next: (value) => memoizedActionsRef.current.onProgressChange(value),
      complete: () => {
        requestStatusRef.current = undefined;
        memoizedActionsRef.current.cancelRequest();
        memoizedActionsRef.current.request(onBuildDone, onBuildError);
        isInitialRequestRef.current = false;
        // LinearProgress has a .4s animation, so wait for it to finish.
        progressResetTimeoutId = setTimeout(() => memoizedActionsRef.current.onProgressChange(undefined), 400);
      },
      error: () => {
        memoizedActionsRef.current.onProgressChange(undefined);
      },
    });

    return () => {
      subscription?.unsubscribe();
      clearTimeout(progressResetTimeoutId);
    };
  }, [onBuildDone, onBuildError, paused, refreshToken, trigger]);

  useEffect(() => {
    const monitor = dragDropManager.getMonitor();
    const unsubscribe = monitor.subscribeToStateChange(() => setIsDragging(monitor.isDragging()));
    return () => unsubscribe();
  }, [dragDropManager]);

  useEffect(
    () => () => {
      memoizedActionsRef.current.onProgressChange(undefined);
    },
    []
  );

  return { doRequest, resetInitialRequestFlag, cancelCurrentAwaiter };
};

export default useDeferredBuild;
